import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Slideshow from '../components/Slideshow';
import Tabs from '../components/Tabs';
import ProductGrid from '../components/ProductGrid';
import Footer from '../components/Footer';
import SupportButton from '../components/SupportButton';
import { Product } from '../types/api';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Home: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();
  const { t } = useTranslation('home');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'all') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Handle cross-domain actions from ecommerce
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'logout') {
      logout();
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
      return;
    }

    // The ?auth=<customToken> handoff is consumed inside AuthContext's
    // mount-time getRedirectResult.finally so loading stays true through
    // signInWithCustomToken — keeps the header skeleton up and prevents the
    // Login button from flashing. URL cleanup happens there too.
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getProducts();
      setProducts(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('load_failed'));
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique types from products
  const productTypes = ['all', ...Array.from(new Set(products.map(p => p.type)))];

  // Filter products by active tab and search query
  const filteredProducts = products.filter(p => {
    const matchesTab = activeTab === 'all' || p.type === activeTab;
    const matchesSearch = !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      <Slideshow />
      <Tabs
        types={productTypes}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        loading={loading}
      />
      <ProductGrid
        products={filteredProducts}
        loading={loading}
        error={error}
        onRetry={fetchProducts}
      />
      <Footer />
      <SupportButton />
    </div>
  );
};

export default Home;
